import { LineString, MultiLineString, MultiPolygon, Polygon, Properties, Feature, Coord } from '@turf/helpers'
import length from '@turf/length'
import lineSlice from '@turf/line-slice'
import centriod from '@turf/centroid'
import pointOnLine from '@turf/nearest-point-on-line'
import { Point } from 'geojson'

export function getDistanceAlongLine(
  line: Feature<LineString, Properties> | LineString,
  point: Coord,
  startPoint?: Coord,
) {
  const snapped = pointOnLine(line, point)

  const coordinates = line.type === 'Feature' ? line.geometry.coordinates : line.coordinates

  const s = lineSlice(startPoint ?? coordinates[0], snapped, line)
  const distance = length(s)

  return distance
}

export function sortByDistanceAlongLine(line: Feature<LineString, Properties> | LineString, points: Coord[]) {
  const withDistances = points.map((point) => ({ point, distance: getDistanceAlongLine(line, point) }))

  withDistances.sort((a, b) => a.distance - b.distance)
  return withDistances.map((wd) => wd.point)
}

// Geometry types that need collapsing to a single marker position. TAS sends
// area warnings as polygons and everything else linear as (Multi)LineString.
type CentrableGeometry = LineString | MultiLineString | Polygon | MultiPolygon

export function getClosestPointToCenter(feature: Feature<CentrableGeometry, Properties>): Point['coordinates'] {
  // this finds the geographic center of any set of lines.
  const centroid = centriod(feature)
  const geometry = feature.geometry

  // Polygons nest their coordinates a level deeper than lines (an array of
  // rings), so pointOnLine finds no segments to walk and hands back its
  // [Infinity, Infinity] sentinel instead of a position. Use the centroid
  // as-is, which also puts the marker inside the area rather than on its
  // edge — matching GeometryField::convertToPoint() on the PHP side.
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    return centroid.geometry.coordinates
  }

  // snaps to the closest point on the Multiline String.
  const snapped = pointOnLine(geometry, centroid)

  // only return coordinates as we want to use the original GeoJson features properties
  return snapped.geometry.coordinates
}

// Journey link coordinates can be empty strings and geocode results can be
// pending, so lat/lng derived from them may be NaN or undefined. Advanced
// Markers reject such positions, so callers use this to skip rendering instead.
export function toLatLngLiteralOrNull(
  lat: number | undefined,
  lng: number | undefined,
): google.maps.LatLngLiteral | null {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

// Gets coordinates from string locations - example "Christchucrh, CBD", or string coordinates
export async function geocodeAddressString(address: string) {
  const geocoder = new google.maps.Geocoder()
  let res: number[] = []
  await geocoder.geocode({ address: address }, (results, status) => {
    try {
      if (results && status === 'OK') {
        res = [results[0].geometry.location.lat(), results[0].geometry.location.lng()]
      }
    } catch (error) {
      console.error('Geocoding failed:', error)
    }
  })
  return res
}
